import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

const FORM_SUBMIT_ADDR = "https://readdy.ai/api/form/db7487gd6mcdj80oo7j0";

const contactMethods = [
  {
    icon: "ri-mail-line",
    label: "客服邮箱",
    value: "service@qingqingcao-video.com",
    desc: "工作日 24 小时内回复",
  },
  {
    icon: "ri-customer-service-2-line",
    label: "客服 QQ",
    value: "800-123-4567",
    desc: "在线实时沟通",
  },
  {
    icon: "ri-phone-line",
    label: "合作电话",
    value: "400-888-6688",
    desc: "周一至周五 9:00 - 18:00",
  },
  {
    icon: "ri-map-pin-line",
    label: "服务范围",
    value: "全网在线服务",
    desc: "内容推荐与信息咨询",
  },
];

export default function ContactPage() {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  useEffect(() => {
    document.title = "联系反馈 - 青青草视频";
    return () => {
      document.title = "青青草视频—高清影视资源在线观看与最新影视推荐";
    };
  }, []);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get("website_alt") || "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      form.reset();
      return;
    }
    formData.delete("website_alt");

    setStatus("loading");
    setFormError("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === "string") body.append(key, value);
      });

      const response = await fetch(FORM_SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: any = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message ||
        parsed?.message ||
        parsed?.meta?.detail ||
        responseText;
      const isSpam =
        typeof serverMsg === "string" && serverMsg.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setFormError("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMsg === "string" && serverMsg
            ? serverMsg
            : "提交失败，请稍后再试"
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络异常，请检查网络连接后重试");
    }
  };

  return (
    <div className="w-full max-w-[1440px] mx-auto px-4 md:px-8 py-8 md:py-10">
      <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-5 flex-wrap">
        <Link to="/" className="hover:text-primary-300 transition-colors cursor-pointer">
          首页
        </Link>
        <i className="ri-arrow-right-s-line" />
        <span className="text-foreground-900">联系反馈</span>
      </nav>

      <header className="mb-8">
        <h1 className="font-heading text-2xl md:text-4xl font-extrabold text-foreground-950">
          联系我们
        </h1>
        <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-2xl leading-relaxed">
          无论是内容建议、合作咨询还是使用反馈，我们都乐意倾听。填写下方表单或通过客服渠道与我们取得联系。
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 md:gap-8">
        <div className="lg:col-span-2 space-y-4">
          {contactMethods.map((method) => (
            <div
              key={method.label}
              className="flex items-start gap-4 rounded-lg bg-background-100 border border-background-200/70 p-5"
            >
              <span className="w-11 h-11 rounded-lg bg-primary-500/15 flex items-center justify-center shrink-0">
                <i className={`${method.icon} text-xl text-primary-400`} />
              </span>
              <div>
                <p className="text-xs text-foreground-500">{method.label}</p>
                <p className="text-sm font-semibold text-foreground-950 mt-0.5 break-all">
                  {method.value}
                </p>
                <p className="text-xs text-foreground-600 mt-1">{method.desc}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="lg:col-span-3">
          <div className="rounded-lg bg-background-100 border border-background-200/70 p-6 md:p-8">
            <h2 className="font-heading text-lg md:text-xl font-bold text-foreground-950 mb-5">
              留言反馈
            </h2>

            {status === "success" && (
              <div className="mb-5 flex items-start gap-3 rounded-md bg-primary-500/15 border border-primary-500/40 p-4">
                <i className="ri-checkbox-circle-fill text-primary-400 text-lg mt-0.5" />
                <p className="text-sm text-foreground-800">
                  提交成功！感谢你的反馈，我们会尽快处理并回复。
                </p>
              </div>
            )}

            {status === "error" && (
              <div className="mb-5 flex items-start gap-3 rounded-md bg-accent-500/15 border border-accent-500/40 p-4">
                <i className="ri-error-warning-fill text-accent-400 text-lg mt-0.5" />
                <p className="text-sm text-foreground-800">{formError}</p>
              </div>
            )}

            <form
              id="contact-feedback-form"
              data-readdy-form
              onSubmit={handleSubmit}
              className="space-y-5"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label
                    htmlFor="contact-name"
                    className="block text-sm text-foreground-700 mb-2"
                  >
                    姓名
                  </label>
                  <input
                    id="contact-name"
                    name="name"
                    type="text"
                    required
                    placeholder="请输入你的称呼"
                    className="w-full h-11 px-4 rounded-md bg-background-200/60 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-500/60 transition-colors"
                  />
                </div>
                <div>
                  <label
                    htmlFor="contact-email"
                    className="block text-sm text-foreground-700 mb-2"
                  >
                    邮箱
                  </label>
                  <input
                    id="contact-email"
                    name="email"
                    type="email"
                    required
                    placeholder="name@example.com"
                    className="w-full h-11 px-4 rounded-md bg-background-200/60 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-500/60 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label
                  htmlFor="contact-subject"
                  className="block text-sm text-foreground-700 mb-2"
                >
                  反馈类型
                </label>
                <select
                  id="contact-subject"
                  name="subject"
                  defaultValue="内容建议"
                  className="w-full h-11 px-4 rounded-md bg-background-200/60 border border-background-300/60 text-sm text-foreground-900 outline-none focus:border-primary-500/60 transition-colors cursor-pointer"
                >
                  <option value="内容建议">内容建议</option>
                  <option value="问题反馈">问题反馈</option>
                  <option value="合作咨询">合作咨询</option>
                  <option value="其他事项">其他事项</option>
                </select>
              </div>

              <div>
                <label
                  htmlFor="contact-message"
                  className="block text-sm text-foreground-700 mb-2"
                >
                  留言内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="请描述你的建议或问题（500 字以内）"
                  className="w-full px-4 py-3 rounded-md bg-background-200/60 border border-background-300/60 text-sm text-foreground-900 placeholder:text-foreground-500 outline-none focus:border-primary-500/60 transition-colors resize-none"
                />
              </div>

              <div className="contact-extra-field" aria-hidden="true">
                <label htmlFor="contact-website-alt">请勿填写此项</label>
                <input
                  id="contact-website-alt"
                  name="website_alt"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  readOnly
                />
              </div>

              <button
                type="submit"
                disabled={status === "loading"}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-8 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
              >
                {status === "loading" ? (
                  <>
                    <i className="ri-loader-4-line animate-spin text-base" />
                    提交中...
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-fill text-base" />
                    提交反馈
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}